import React, { useEffect, useRef, useState } from 'react'
import { X, Camera, ImagePlus, Loader2, ScanLine, RotateCw, Maximize2, ArrowLeft, Check, Trash2, Download, Paperclip } from 'lucide-react'
import { assignmentApi } from '../api/lessonBuilderApi'
import { loadSource, canvasToBlob, warpQuad, rotate90, applyFilter, saveToDevice, DEFAULT_QUAD, FULL_QUAD, type Quad, type ScanFilter } from '../lib/docScan'

// Máy scan bài làm tự luận: chụp → tự tìm tờ giấy (OpenCV trên server) → kéo chỉnh 4 góc
// → nắn thẳng + tăng nét → nhiều trang → lưu vào máy và/hoặc đính kèm vào bài nộp.

interface Page { file: File; url: string }

const SAVE_PREF_KEY = 'vl365_scan_save_to_device'
const readSavePref = () => { try { return localStorage.getItem(SAVE_PREF_KEY) !== '0' } catch { return true } }
const writeSavePref = (v: boolean) => { try { localStorage.setItem(SAVE_PREF_KEY, v ? '1' : '0') } catch { /* chế độ ẩn danh */ } }

const FILTERS: { key: ScanFilter; label: string }[] = [
  { key: 'enhance', label: 'Tăng nét' },
  { key: 'bw', label: 'Đen trắng' },
  { key: 'original', label: 'Gốc' },
]

const stamp = () => {
  const d = new Date(); const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
}
// Cho spinner kịp hiện trước khi chạy việc nặng trên luồng chính.
const nextFrame = () => new Promise<void>((r) => setTimeout(r, 30))

interface Props {
  onClose: () => void
  upload?: (file: File) => Promise<string>      // tải 1 trang lên → url
  onAttached?: (urls: string[]) => void         // gắn các url vào bài nộp
  // Chế độ 1 trang (quét phiếu đáp án): xong trang là trả file ngay, không lưu/đính kèm.
  onFinish?: (file: File) => void
  // Tự cắt (phiếu đáp án có 4 ô đen): trả quad → nắn và trả file ngay, bỏ qua bước kéo góc.
  // Trả null → quy trình cũ (tìm mép giấy + kéo 4 góc bằng tay).
  autoQuad?: (source: HTMLCanvasElement) => Quad | null
  title?: string
}

const ScanCapture: React.FC<Props> = ({ onClose, upload, onAttached, onFinish, autoQuad, title }) => {
  const [stage, setStage] = useState<'pick' | 'adjust' | 'review'>('pick')
  const [source, setSource] = useState<HTMLCanvasElement | null>(null)
  const [sourceUrl, setSourceUrl] = useState('')
  const [quad, setQuad] = useState<Quad>(DEFAULT_QUAD)
  const [warped, setWarped] = useState<HTMLCanvasElement | null>(null)
  const [filter, setFilter] = useState<ScanFilter>('enhance')
  const [turns, setTurns] = useState(0)
  const [result, setResult] = useState<HTMLCanvasElement | null>(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [pages, setPages] = useState<Page[]>([])
  const [saveToPhone, setSaveToPhone] = useState(readSavePref)
  const [busy, setBusy] = useState('')      // mô tả việc đang làm; rỗng = rảnh
  const [msg, setMsg] = useState('')
  const smallRef = useRef<Blob | null>(null)  // ảnh thu nhỏ gửi server dò giấy
  const boxRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<number | null>(null)
  const pagesRef = useRef(pages); pagesRef.current = pages

  useEffect(() => () => pagesRef.current.forEach((p) => URL.revokeObjectURL(p.url)), [])
  useEffect(() => () => { if (sourceUrl) URL.revokeObjectURL(sourceUrl) }, [sourceUrl])
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])

  // Ảnh kết quả: chạy lại khi đổi bộ lọc / xoay.
  useEffect(() => {
    if (!warped) return
    let cancelled = false
    ;(async () => {
      setBusy('Đang xử lý ảnh…'); await nextFrame()
      try {
        const out = applyFilter(rotate90(warped, turns), filter)
        const preview = await canvasToBlob(out, 0.85, 1400)
        if (cancelled) return
        setResult(out); setPreviewUrl(URL.createObjectURL(preview))
      } catch { if (!cancelled) setMsg('Xử lý ảnh lỗi — hãy chụp lại.') } finally { if (!cancelled) setBusy('') }
    })()
    return () => { cancelled = true }
  }, [warped, filter, turns])

  const detect = async () => {
    const small = smallRef.current
    if (!small) return
    setBusy('Đang tìm tờ giấy…')
    try {
      const r = await assignmentApi.detectPaper(new File([small], 'scan.jpg', { type: 'image/jpeg' }))
      if (r.found && r.corners) { setQuad(r.corners); setMsg('Đã tìm thấy tờ giấy — kéo các góc nếu chưa khớp.') }
      else { setQuad(DEFAULT_QUAD); setMsg('Chưa tự tìm được mép giấy — hãy kéo 4 góc cho khớp tờ giấy.') }
    } catch {
      setQuad(DEFAULT_QUAD); setMsg('Không kết nối được máy dò giấy — hãy kéo 4 góc bằng tay.')
    } finally { setBusy('') }
  }

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = ''
    if (!f) return
    if (!f.type.startsWith('image/')) { setMsg('Chỉ scan được ảnh (PDF hãy dùng nút "Tải lên").'); return }
    setBusy('Đang mở ảnh…'); setMsg('')
    try {
      const src = await loadSource(f)
      if (autoQuad && onFinish) {
        const q = autoQuad(src)
        if (q) {
          // Trả ẢNH GỐC (đã giới hạn cỡ): bước đọc tự nắn theo 4 ô đen — nắn 2 lần + nén JPEG giữa chừng làm
          // đường kẻ mờ mất. (q chỉ dùng để biết đã thấy đủ 4 ô đen.)
          setBusy('Đã thấy 4 ô đen — đang đọc phiếu…'); await nextFrame()
          const blob = await canvasToBlob(src, 0.95)
          setBusy('')
          onFinish(new File([blob], `phieu-${stamp()}.jpg`, { type: 'image/jpeg' }))
          return
        }
      }
      const small = await canvasToBlob(src, 0.85, 1200)
      smallRef.current = small
      setSource(src); setSourceUrl(URL.createObjectURL(small)); setQuad(DEFAULT_QUAD); setStage('adjust')
    } catch { setMsg('Không đọc được ảnh, hãy chụp lại.'); setBusy(''); return }
    await detect()
    if (autoQuad && onFinish) setMsg((m) => `Chưa thấy rõ đủ 4 ô đen ở góc phiếu (chụp lại cho thấy trọn 4 ô đen sẽ tự cắt). ${m}`)
  }

  const resetCapture = () => {
    setStage('pick'); setSource(null); setSourceUrl(''); setWarped(null); setResult(null); setPreviewUrl('')
    smallRef.current = null
  }

  const toReview = async () => {
    if (!source) return
    setBusy('Đang nắn thẳng trang…'); setMsg(''); await nextFrame()
    try { setTurns(0); setWarped(warpQuad(source, quad)); setStage('review') }
    catch { setMsg('Không nắn được trang — hãy kéo lại 4 góc.'); setBusy('') }
  }

  const backToAdjust = () => { setWarped(null); setResult(null); setPreviewUrl(''); setStage('adjust') }

  const addPage = async () => {
    if (!result) return
    setBusy('Đang lưu trang…')
    try {
      const blob = await canvasToBlob(result, 0.92)
      const n = pages.length + 1
      const file = new File([blob], `bai-lam-${stamp()}-trang-${n}.jpg`, { type: 'image/jpeg' })
      if (onFinish) { onFinish(file); return }
      setPages((p) => [...p, { file, url: URL.createObjectURL(blob) }])
      resetCapture()
      setMsg(`Đã thêm trang ${n}. Chụp trang tiếp theo, hoặc lưu / đính kèm bên dưới.`)
    } finally { setBusy('') }
  }

  const removePage = (i: number) => setPages((p) => {
    URL.revokeObjectURL(p[i].url)
    return p.filter((_, k) => k !== i)
  })

  // Handle góc: pointer capture → kéo mượt cả chuột lẫn ngón tay.
  const onHandleDown = (i: number) => (e: React.PointerEvent) => {
    e.preventDefault(); (e.currentTarget as Element).setPointerCapture(e.pointerId); dragRef.current = i
  }
  const onHandleMove = (e: React.PointerEvent) => {
    const i = dragRef.current; const box = boxRef.current
    if (i === null || !box) return
    const r = box.getBoundingClientRect()
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))
    setQuad((q) => q.map((p, k) => (k === i ? [x, y] : p)) as Quad)
  }
  const onHandleUp = () => { dragRef.current = null }

  const files = pages.map((p) => p.file)

  // Share phải chạy NGAY trong sự kiện bấm (Safari chặn nếu đã await việc khác trước).
  const onSaveOnly = async () => {
    const r = await saveToDevice(files)
    setMsg(r === 'shared' ? 'Đã mở bảng chia sẻ — chọn "Lưu hình ảnh" để lưu vào máy.'
      : r === 'downloaded' ? 'Đã tải ảnh về máy (thư mục Tải xuống).' : 'Đã huỷ lưu ảnh.')
  }

  const onAttach = async () => {
    if (!upload || !onAttached) return
    if (saveToPhone) await saveToDevice(files).catch(() => 'cancelled')
    const urls: string[] = []
    try {
      for (let i = 0; i < files.length; i++) {
        setBusy(`Đang tải lên trang ${i + 1}/${files.length}…`)
        urls.push(await upload(files[i]))
      }
      onAttached(urls)
      onClose()
    } catch {
      // Giữ lại các trang CHƯA tải lên để bấm lại; trang đã lên thì gắn luôn.
      if (urls.length) { onAttached(urls); setPages((p) => p.slice(urls.length)) }
      setMsg(`Tải lên chưa xong (${urls.length}/${files.length} trang). Kiểm tra mạng rồi bấm lại.`)
    } finally { setBusy('') }
  }

  const close = () => {
    if (pages.length && !window.confirm(`Bỏ ${pages.length} trang đã scan mà chưa đính kèm?`)) return
    onClose()
  }

  const btn = 'inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold disabled:opacity-50'
  const spinner = busy && (
    <div className="absolute inset-0 flex items-center justify-center bg-white/60">
      <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white shadow text-sm text-slate-700"><Loader2 size={16} className="animate-spin text-teal-600" /> {busy}</span>
    </div>
  )

  return (
    <div className="fixed inset-0 z-[300] bg-slate-900/60 flex sm:items-center sm:justify-center">
      <div className="bg-[#fdfcf0] w-full h-full sm:h-auto sm:max-h-[95vh] sm:max-w-2xl sm:rounded-2xl shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-100 bg-white">
          <ScanLine size={18} className="text-teal-600" />
          <h3 className="font-bold text-slate-800 flex-1">{title || 'Scan bài làm'}{pages.length ? ` · ${pages.length} trang` : ''}</h3>
          <button onClick={close} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Đóng"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {stage === 'pick' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className={`${btn} flex-col py-5 bg-teal-600 text-white hover:bg-teal-700 cursor-pointer ${busy ? 'pointer-events-none opacity-50' : ''}`}>
                  <Camera size={26} /> {onFinish ? 'Chụp phiếu' : `Chụp trang ${pages.length + 1}`}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
                </label>
                <label className={`${btn} flex-col py-5 bg-white border border-teal-200 text-teal-700 hover:bg-teal-50 cursor-pointer ${busy ? 'pointer-events-none opacity-50' : ''}`}>
                  <ImagePlus size={26} /> Chọn ảnh có sẵn
                  <input type="file" accept="image/*" className="hidden" onChange={onPick} />
                </label>
              </div>
              {busy && <p className="text-sm text-slate-600 inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> {busy}</p>}
              {!pages.length && (
                <p className="text-xs text-slate-500 leading-relaxed">Mẹo: đặt tờ giấy trên nền <strong>tối hơn giấy</strong> (mặt bàn, bìa sách), đủ sáng, chụp thẳng từ trên xuống để máy tự nhận đúng mép giấy.</p>
              )}
              {pages.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {pages.map((p, i) => (
                    <div key={p.url} className="relative rounded-lg overflow-hidden border border-slate-200 bg-white">
                      <img src={p.url} alt={`Trang ${i + 1}`} className="w-full aspect-[3/4] object-cover" />
                      <span className="absolute left-1 top-1 px-1.5 rounded bg-teal-600 text-white text-xs font-bold">{i + 1}</span>
                      <button onClick={() => removePage(i)} className="absolute right-1 top-1 p-1 rounded bg-white/90 text-rose-500 hover:text-rose-700" aria-label={`Xoá trang ${i + 1}`}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {stage === 'adjust' && (
            <>
              <div className="text-center">
                <div ref={boxRef} className="relative inline-block max-w-full select-none" style={{ touchAction: 'none' }}>
                  <img src={sourceUrl} alt="Ảnh chụp" draggable={false} className="block max-w-full max-h-[62vh]" />
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <polygon points={quad.map(([x, y]) => `${x * 100},${y * 100}`).join(' ')}
                      fill="rgba(20,184,166,0.15)" stroke="#0d9488" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                  </svg>
                  {quad.map(([x, y], i) => (
                    <div key={i} onPointerDown={onHandleDown(i)} onPointerMove={onHandleMove} onPointerUp={onHandleUp} onPointerCancel={onHandleUp}
                      className="absolute w-10 h-10 -ml-5 -mt-5 rounded-full border-[3px] border-teal-600 bg-white/50 flex items-center justify-center cursor-grab"
                      style={{ left: `${x * 100}%`, top: `${y * 100}%`, touchAction: 'none' }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-700" />
                    </div>
                  ))}
                  {spinner}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={resetCapture} disabled={!!busy} className={`${btn} bg-white border border-slate-200 text-slate-600 hover:bg-slate-50`}><ArrowLeft size={15} /> Chụp lại</button>
                <button onClick={detect} disabled={!!busy} className={`${btn} bg-white border border-teal-200 text-teal-700 hover:bg-teal-50`}><ScanLine size={15} /> Tự tìm lại</button>
                <button onClick={() => setQuad(FULL_QUAD)} disabled={!!busy} className={`${btn} bg-white border border-slate-200 text-slate-600 hover:bg-slate-50`}><Maximize2 size={15} /> Cả ảnh</button>
                <button onClick={toReview} disabled={!!busy} className={`${btn} ml-auto bg-teal-600 text-white hover:bg-teal-700`}><Check size={15} /> Tiếp tục</button>
              </div>
            </>
          )}

          {stage === 'review' && (
            <>
              <div className="relative text-center min-h-[200px]">
                {previewUrl && <img src={previewUrl} alt="Trang đã scan" className="inline-block max-w-full max-h-[58vh] border border-slate-200 shadow-sm bg-white" />}
                {spinner}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map((f) => (
                  <button key={f.key} onClick={() => setFilter(f.key)} disabled={!!busy}
                    className={`${btn} ${filter === f.key ? 'bg-amber-400 text-slate-900' : 'bg-white border border-amber-200 text-amber-800 hover:bg-amber-50'}`}>{f.label}</button>
                ))}
                <button onClick={() => setTurns((t) => t + 1)} disabled={!!busy} className={`${btn} bg-white border border-slate-200 text-slate-600 hover:bg-slate-50`} aria-label="Xoay 90 độ"><RotateCw size={15} /> Xoay</button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={backToAdjust} disabled={!!busy} className={`${btn} bg-white border border-slate-200 text-slate-600 hover:bg-slate-50`}><ArrowLeft size={15} /> Chỉnh góc</button>
                <button onClick={addPage} disabled={!!busy || !result} className={`${btn} ml-auto bg-teal-600 text-white hover:bg-teal-700`}><Check size={15} /> {onFinish ? 'Dùng ảnh này' : 'Xong trang này'}</button>
              </div>
            </>
          )}

          {msg && <p className="text-sm text-slate-700">{msg}</p>}
        </div>

        {stage === 'pick' && pages.length > 0 && (
          <div className="border-t border-amber-100 bg-white px-4 py-3 space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={saveToPhone} onChange={(e) => { setSaveToPhone(e.target.checked); writeSavePref(e.target.checked) }} className="w-4 h-4 accent-teal-600" />
              Đồng thời lưu ảnh vào máy
            </label>
            <div className="flex flex-wrap gap-2">
              <button onClick={onSaveOnly} disabled={!!busy} className={`${btn} bg-white border border-teal-200 text-teal-700 hover:bg-teal-50`}><Download size={15} /> Chỉ lưu vào máy</button>
              <button onClick={onAttach} disabled={!!busy} className={`${btn} ml-auto bg-blue-600 text-white hover:bg-blue-700`}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />} Đính kèm {pages.length} trang vào bài làm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ScanCapture
